import type React from 'react';

import { Box, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { CodeBaseStatesModel } from '../../../api/api';
import DeploymentStatus from '../../CodeGen/Components/DeploymentStatus';

export type DeployableCodebaseType = 'domain' | 'application';

export interface LayerDeploymentPanelProps {
	activeCodebaseType: DeployableCodebaseType;
	domainCodebaseState?: CodeBaseStatesModel;
	applicationCodebaseState?: CodeBaseStatesModel;
	onActiveCodebaseTypeChange: (codebaseType: DeployableCodebaseType) => void;
	onAdvance: (next: CodeBaseStatesModel, current: CodeBaseStatesModel) => void | Promise<void>;
	loading: boolean;
	operationErrors?: string[] | null;
	operationErrorTitle?: React.ReactNode;
	operationErrorSubtitle?: React.ReactNode;
}

const stateColor = (state?: CodeBaseStatesModel) => {
	switch (state) {
		case CodeBaseStatesModel.Changed:
			return 'warning.main';
		case CodeBaseStatesModel.Packaged:
			return 'info.main';
		case CodeBaseStatesModel.Deployed:
			return 'success.main';
		default:
			return 'text.disabled';
	}
};

type LayerOptionProps = {
	value: DeployableCodebaseType;
	label: string;
	state?: CodeBaseStatesModel;
};

function LayerOption({ value, label, state }: LayerOptionProps) {
	return (
		<ToggleButton value={value} aria-label={`Deploy ${label}`} sx={{ minWidth: 96, px: 1, py: 0.45, textTransform: 'none' }}>
			<Stack spacing={0.15} alignItems="flex-start" sx={{ minWidth: 0 }}>
				<Typography sx={{ fontSize: '0.7rem', fontWeight: 700, lineHeight: 1.15 }}>{label}</Typography>
				<Stack direction="row" spacing={0.5} alignItems="center">
					<Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: stateColor(state), flexShrink: 0 }} />
					<Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', lineHeight: 1.15 }}>
						{state ?? 'Unavailable'}
					</Typography>
				</Stack>
			</Stack>
		</ToggleButton>
	);
}

export default function LayerDeploymentPanel({
	activeCodebaseType,
	domainCodebaseState,
	applicationCodebaseState,
	onActiveCodebaseTypeChange,
	onAdvance,
	loading,
	operationErrors,
	operationErrorTitle,
	operationErrorSubtitle,
}: LayerDeploymentPanelProps) {
	const activeCodebaseState = activeCodebaseType === 'domain' ? domainCodebaseState : applicationCodebaseState;

	return (
		<Stack
			direction="row"
			alignItems="center"
			spacing={1}
			sx={{
				alignSelf: 'stretch',
				flexShrink: 0,
				px: 1,
				py: 0.5,
				border: 0,
				borderRadius: 0,
				bgcolor: 'transparent',
				boxShadow: 'none',
			}}
		>
			<Tooltip title="Choose which codebase the package or deploy action applies to">
				<ToggleButtonGroup
					exclusive
					size="small"
					value={activeCodebaseType}
					onChange={(_, next: DeployableCodebaseType | null) => {
						if (next) onActiveCodebaseTypeChange(next);
					}}
					aria-label="Package and deploy target"
					sx={(theme) => ({
						'& .MuiToggleButton-root.Mui-selected': {
							color: 'primary.main',
							borderColor: 'primary.main',
							backgroundColor: alpha(theme.palette.primary.main, 0.1),
						},
					})}
				>
					<LayerOption value="domain" label="Domain Model" state={domainCodebaseState} />
					<LayerOption value="application" label="Application" state={applicationCodebaseState} />
				</ToggleButtonGroup>
			</Tooltip>

			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', minWidth: 'max-content' }}>
				<DeploymentStatus
					state={activeCodebaseState ?? CodeBaseStatesModel.Deployed}
					showLabels={true}
					onAdvance={onAdvance}
					disabled={!activeCodebaseState}
					loading={loading}
					variant="header"
					operationErrors={operationErrors}
					operationErrorTitle={operationErrorTitle}
					operationErrorSubtitle={operationErrorSubtitle}
				/>
			</Box>
		</Stack>
	);
}
